// src/components/accesFamille/apiAccesFamille.js
// Appels de l'administration vers /api/acces-famille (jeton employé).

import { getAuthHeaders } from '../../services/api';

const RACINE = '/api/acces-famille';

async function appeler(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: getAuthHeaders(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) {
    const erreur = new Error(donnees?.message || (reponse.status === 403
      ? "L'espace famille n'est pas ouvert pour cet établissement."
      : 'Une erreur est survenue.'));
    erreur.code = donnees?.code;
    erreur.statut = reponse.status;
    throw erreur;
  }
  return donnees;
}

/**
 * Les en-têtes d'un envoi de fichier : ceux de la maison, MOINS le type.
 *
 * ⚠️ C'EST LE POINT DUR DE CE CHANTIER. `getAuthHeaders()` pose en dur
 * `Content-Type: application/json` (src/services/api.js). Laissé sur un
 * `FormData`, il annonce du JSON et surtout il EMPÊCHE le navigateur d'écrire
 * la frontière des parties (`multipart/form-data; boundary=…`) : le serveur
 * reçoit un corps qu'aucun analyseur multipart ne sait lire, et ne voit donc
 * aucun fichier. On retire la clé plutôt que d'en reconstruire la liste, pour
 * que `X-Tenant` et l'année scolaire suivent toutes seules si elles changent.
 */
function entetesSansType() {
  const entetes = { ...getAuthHeaders() };
  delete entetes['Content-Type'];
  return entetes;
}

/**
 * Un message avec sa pièce jointe. Le FICHIER EST AJOUTÉ EN DERNIER : les
 * champs de texte arrivent alors au serveur avant le flux, dans l'ordre où
 * `multer` les lit.
 */
function formulaireMessage(champs, fichier) {
  const formulaire = new FormData();
  for (const [cle, valeur] of Object.entries(champs)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') formulaire.append(cle, String(valeur));
  }
  formulaire.append('nom_piece', fichier.name);
  formulaire.append('fichier', fichier);
  return formulaire;
}

/** Même jeton, mêmes erreurs que `appeler`, mais un corps multipart. */
async function envoyerFormulaire(chemin, formulaire) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: 'POST',
      headers: entetesSansType(),
      body: formulaire
    });
  } catch {
    throw new Error("Connexion impossible : le fichier n'a pas pu être envoyé.");
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) {
    const erreur = new Error(donnees?.message || "La pièce jointe n'a pas pu être envoyée.");
    erreur.code = donnees?.code;
    erreur.statut = reponse.status;
    throw erreur;
  }
  return donnees;
}

async function telechargerPdf(chemin, corps, nom) {
  const reponse = await fetch(`${RACINE}${chemin}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(corps)
  });
  if (!reponse.ok) {
    let message = 'Impossible de préparer la fiche.';
    try { message = (await reponse.json())?.message || message; } catch { /* rien */ }
    throw new Error(message);
  }
  const blob = await reponse.blob();
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export const accesFamille = {
  droits: () => appeler('/droits'),
  eleve: (eleveId) => appeler(`/eleves/${eleveId}`),
  creerCode: (eleveId, donnees) => appeler(`/eleves/${eleveId}/codes`, { methode: 'POST', corps: donnees }),
  annulerCode: (codeId) => appeler(`/codes/${codeId}/annuler`, { methode: 'POST' }),
  fiche: (code) => telechargerPdf('/codes/fiche', { code }, `acces-espace-famille-${String(code).replace(/[^A-Z0-9]/gi, '')}.pdf`),
  revoquer: (lienId, motif) => appeler(`/liens/${lienId}/revoquer`, { methode: 'POST', corps: { motif } }),
  reinitialiser: (compteId) => appeler(`/comptes/${compteId}/reinitialisation`, { methode: 'POST' }),
  statutCompte: (compteId, statut, motif) => appeler(`/comptes/${compteId}/statut`, { methode: 'POST', corps: { statut, motif } }),

  nonLus: () => appeler('/messages/non-lus'),
  piece: async (pieceId, nom) => {
    const reponse = await fetch(`${RACINE}/messages/pieces/${pieceId}`, { headers: getAuthHeaders() });
    if (!reponse.ok) throw new Error('Cette pièce jointe est introuvable.');
    const blob = await reponse.blob();
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nom || 'piece-jointe';
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  },
  messages: (filtres = {}) => {
    const params = new URLSearchParams(Object.entries(filtres).filter(([, v]) => v));
    return appeler(`/messages${params.toString() ? `?${params}` : ''}`);
  },

  // Les familles a qui l'ecole peut ecrire : celles qui ont un espace actif.
  comptesFamilles: (recherche = '') => {
    const params = new URLSearchParams(recherche ? { recherche } : {});
    return appeler(`/comptes-familles${params.toString() ? `?${params}` : ''}`);
  },

  // L'ecole ouvre une conversation, au lieu d'attendre que le parent ecrive.
  // Avec un fichier, le corps devient multipart ; sans, il reste du JSON - la
  // route accepte les deux, et on n'impose pas un formulaire pour rien.
  nouveauMessage: (donnees, fichier = null) => (fichier
    ? envoyerFormulaire('/messages', formulaireMessage(donnees, fichier))
    : appeler('/messages', { methode: 'POST', corps: donnees })),
  conversation: (filId) => appeler(`/messages/${filId}`),
  repondre: (filId, contenu, fichier = null) => (fichier
    ? envoyerFormulaire(`/messages/${filId}/repondre`, formulaireMessage({ contenu }, fichier))
    : appeler(`/messages/${filId}/repondre`, { methode: 'POST', corps: { contenu } })),
  statutConversation: (filId, statut) => appeler(`/messages/${filId}/statut`, { methode: 'POST', corps: { statut } })
};
